import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { WidgetAction } from '@pickonegift/shared';
import { useSEO } from '../hooks/useSEO';

interface DemoProduct {
    title: string;
    imageUrl: string;
    price: string;
    oldPrice?: string;
    badge?: string;
    description: string;
}

const DEMO_PRODUCTS: DemoProduct[] = [
    {
        title: 'Sony WH-1000XM5',
        imageUrl: '/images/3771f672b6.jpg',
        price: '1 499 zł',
        oldPrice: '1 799 zł',
        badge: 'Bestseller',
        description: 'Bezprzewodowe słuchawki z najlepszym ANC na rynku. 30h baterii.',
    },
    {
        title: 'Kindle Paperwhite 2024',
        imageUrl: '/images/29ebfbdd8b.jpg',
        price: '699 zł',
        description: 'Czytnik e-booków z 6.8" wyświetlaczem i ciepłym podświetleniem.',
    },
    {
        title: 'Lego Technic Porsche 911',
        imageUrl: '/images/fallback-gift.jpg',
        price: '849 zł',
        badge: 'Nowość',
        description: '1 458 elementów. Idealny prezent dla fana motoryzacji.',
    },
    {
        title: 'Dyson Airwrap',
        imageUrl: '/images/fallback-gift.jpg',
        price: '2 299 zł',
        oldPrice: '2 699 zł',
        description: 'Wielofunkcyjna stylizacja włosów bez ekstremalnych temperatur.',
    },
    {
        title: 'Nintendo Switch OLED',
        imageUrl: '/images/e094a9d973.jpg',
        price: '1 549 zł',
        badge: 'Popularny',
        description: 'Konsola hybrydowa z 7" ekranem OLED.',
    },
    {
        title: 'Yeti Rambler 30oz',
        imageUrl: '/images/2a7f676a7d.jpg',
        price: '189 zł',
        description: 'Kubek termiczny z podwójną izolacją próżniową. Utrzymuje temperaturę do 12h.',
    },
];

export default function Demo() {
    useSEO(
        'Platforma B2B — Demo e-commerce',
        'Zintegruj narzędzie PickOneGift na swoim e-commerce sklepie. Sprawdź, jak może wyglądać natywny widget do zaufanego przekazywania prezentów.'
    );
    const [widgetItems, setWidgetItems] = useState<string[]>([]);
    const [widgetOpen, setWidgetOpen] = useState(false);
    const [widgetIframe, setWidgetIframe] = useState<HTMLIFrameElement | null>(null);

    // Listen for postMessage from widget iframe
    const handleMessage = useCallback((event: MessageEvent) => {
        const { type, payload } = event.data || {};
        if (type === WidgetAction.ITEMS_UPDATED && Array.isArray(payload)) {
            setWidgetItems(payload.map((p: { title: string }) => p.title));
        }
        if (type === WidgetAction.READY) {
            // Widget is loaded
        }
    }, []);

    useEffect(() => {
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [handleMessage]);

    const addToPickOneGift = (product: DemoProduct) => {
        if (widgetIframe?.contentWindow) {
            widgetIframe.contentWindow.postMessage(
                {
                    type: WidgetAction.ADD_ITEM,
                    payload: {
                        title: product.title,
                        imageUrl: product.imageUrl,
                        productUrl: window.location.href,
                        price: product.price,
                    },
                },
                '*',
            );
        }
        setWidgetOpen(true);
    };

    return (
        <div className="page">
            <Header />

            {/* Demo Banner */}
            <div
                style={{
                    background: 'linear-gradient(135deg, rgba(124, 92, 252, 0.15) 0%, rgba(255, 107, 157, 0.1) 100%)',
                    borderBottom: '1px solid rgba(124, 92, 252, 0.2)',
                    padding: '16px 24px',
                    textAlign: 'center',
                }}
            >
                <span style={{ fontSize: '0.9rem', color: 'var(--color-primary-light)' }}>
                    🎁 <strong>Demo:</strong> To jest symulacja sklepu z widgetem PickOneGift.{' '}
                    <Link to="/create" style={{ color: 'var(--color-accent)' }}>
                        Chcesz zintegrować? →
                    </Link>
                </span>
            </div>

            {/* B2B Partner Promo */}
            <div style={{ background: 'rgba(255,255,255,0.03)', padding: '16px 24px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                    📈 <strong>Jesteś właścicielem e-commerce?</strong> Zwiększ sprzedaż i konwersję poprzez listy życzeń z tagami UTM.
                    <Link to="/partner/dashboard" className="btn btn-primary" style={{ marginLeft: '16px', padding: '6px 12px', fontSize: '0.85rem' }}>
                        Zobacz Panel Sklepu
                    </Link>
                </p>
            </div>

            {/* Fake Store Header */}
            <div
                style={{
                    background: 'var(--color-bg-secondary)',
                    borderBottom: '1px solid var(--color-border)',
                    padding: '20px 24px',
                }}
            >
                <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '1.5rem' }}>🏪</span>
                        <span style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-text)' }}>
                            DemoShop.pl
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '0.9rem', color: 'var(--color-text-secondary)', flexWrap: 'wrap' }}>
                        <span>Elektronika</span>
                        <span>Zabawki</span>
                        <span>Dom</span>
                        <span>Bestsellery</span>
                    </div>
                </div>
            </div>

            {/* Product Grid */}
            <div className="container" style={{ padding: '40px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
                    <h2 style={{ fontSize: '1.5rem' }}>🎁 Pomysły na prezent</h2>
                    {widgetItems.length > 0 && (
                        <span
                            style={{
                                background: 'var(--gradient-primary)',
                                color: 'white',
                                padding: '6px 16px',
                                borderRadius: '999px',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                            }}
                        >
                            {widgetItems.length} na liście PickOneGift
                        </span>
                    )}
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                        gap: '24px',
                    }}
                >
                    {DEMO_PRODUCTS.map((product, i) => {
                        const isAdded = widgetItems.includes(product.title);
                        return (
                            <div
                                key={i}
                                className="card"
                                style={{
                                    overflow: 'hidden',
                                    padding: 0,
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                }}
                            >
                                <div style={{ position: 'relative' }}>
                                    <img
                                        src={product.imageUrl}
                                        alt={product.title}
                                        style={{ width: '100%', height: '220px', objectFit: 'cover' }}
                                        loading="lazy"
                                    />
                                    {product.badge && (
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: '12px',
                                                left: '12px',
                                                background: 'var(--gradient-primary)',
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '999px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {product.badge}
                                        </span>
                                    )}
                                </div>
                                <div style={{ padding: '20px' }}>
                                    <h3 style={{ fontSize: '1.05rem', marginBottom: '6px' }}>{product.title}</h3>
                                    <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '12px', lineHeight: 1.5 }}>
                                        {product.description}
                                    </p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-primary-light)' }}>
                                            {product.price}
                                        </span>
                                        {product.oldPrice && (
                                            <span
                                                style={{
                                                    fontSize: '0.85rem',
                                                    color: 'var(--color-text-muted)',
                                                    textDecoration: 'line-through',
                                                }}
                                            >
                                                {product.oldPrice}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <button
                                            className="btn btn-secondary"
                                            style={{ flex: 1, fontSize: '0.85rem', padding: '10px 16px', minWidth: '100px' }}
                                        >
                                            🛒 Do koszyka
                                        </button>
                                        <button
                                            className={`btn ${isAdded ? 'btn-ghost' : 'btn-primary'}`}
                                            onClick={() => addToPickOneGift(product)}
                                            style={{ flex: 1, fontSize: '0.85rem', padding: '10px 16px', minWidth: '100px' }}
                                            disabled={isAdded}
                                        >
                                            {isAdded ? '✓ Dodano' : '🎁 PickOneGift'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Integration Code Preview */}
                <div className="glass" style={{ marginTop: '48px', padding: '32px' }}>
                    <h3 style={{ marginBottom: '16px' }}>💻 Kod integracji</h3>
                    <p style={{ color: 'var(--color-text-secondary)', marginBottom: '16px', fontSize: '0.9rem' }}>
                        Aby dodać widget PickOneGift do swojego sklepu, wystarczy jeden tag <code>&lt;script&gt;</code>:
                    </p>
                    <pre
                        style={{
                            background: 'rgba(0,0,0,0.3)',
                            padding: '20px',
                            borderRadius: '8px',
                            overflow: 'auto',
                            fontSize: '0.85rem',
                            lineHeight: 1.6,
                            color: '#a5d6ff',
                            border: '1px solid var(--color-border)',
                        }}
                    >
                        {`<!-- Dodaj przed </body> -->
<script
  src="https://cdn.pickonegift.com/widget.js"
  data-company-id="TWOJ_ID"
  data-color="#7c5cfc"
></script>

<!-- Na przyciskach produktów dodaj: -->
<button
  data-pickonegift-add
  data-pickonegift-title="Nazwa produktu"
  data-pickonegift-image="https://url-do-zdjecia.jpg"
  data-pickonegift-price="99 zł"
>
  🎁 Dodaj do listy prezentów
</button>`}
                    </pre>
                    <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <Link to="/terms" className="btn btn-ghost" style={{ fontSize: '0.85rem' }}>
                            📋 Regulamin integracji
                        </Link>
                        <a href="mailto:partners@pickonegift.com" className="btn btn-primary" style={{ fontSize: '0.85rem' }}>
                            📧 Skontaktuj się z nami
                        </a>
                    </div>
                </div>
            </div>

            {/* Widget Simulation Panel */}
            <div
                style={{
                    position: 'fixed',
                    bottom: '96px',
                    right: '24px',
                    width: '380px',
                    maxWidth: 'calc(100vw - 48px)',
                    maxHeight: 'min(520px, calc(100vh - 120px))',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.5)',
                    zIndex: 2147483647,
                    display: widgetOpen ? 'block' : 'none',
                    background: '#0f0f23',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    animation: 'fadeIn 0.25s ease',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 16px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                >
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>🎁 PickOneGift</span>
                    <button
                        onClick={() => setWidgetOpen(false)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#9090b0',
                            cursor: 'pointer',
                            fontSize: '18px',
                            padding: '4px',
                        }}
                    >
                        ✕
                    </button>
                </div>
                <iframe
                    ref={(el) => setWidgetIframe(el)}
                    src="/widget?companyId=demo-shop"
                    style={{ width: '100%', height: '480px', border: 'none', background: '#0f0f23' }}
                    title="PickOneGift Widget"
                    allow="clipboard-write"
                />
            </div>

            {/* Floating Bubble */}
            <button
                onClick={() => setWidgetOpen((o) => !o)}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #7c5cfc 0%, #ff6b9d 100%)',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 4px 20px rgba(124, 92, 252, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    zIndex: 2147483646,
                    transition: 'transform 0.2s ease',
                }}
                onMouseEnter={(e) => ((e.target as HTMLElement).style.transform = 'scale(1.1)')}
                onMouseLeave={(e) => ((e.target as HTMLElement).style.transform = 'scale(1)')}
            >
                🎁
                {widgetItems.length > 0 && (
                    <span
                        style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            minWidth: '22px',
                            height: '22px',
                            borderRadius: '11px',
                            background: '#ff6b9d',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 5px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                        }}
                    >
                        {widgetItems.length}
                    </span>
                )}
            </button>

            <Footer />
        </div>
    );
}
